"use client";

import { useCallback, useEffect, useRef, useState } from "react";

// Shared state for admin list pages: debounced search, optional extra filters, server
// pagination/sorting, and a delete-confirm target. Deduplicates the near-identical state
// machine that help-admin / passport-admin / cortex-admin each hand-rolled.
//
//   const t = useDataTableState<User>({
//     fetcher: (p) => adminApi.users.list(p),          // p has page/pageSize/search/sort + filters
//     onDelete: (u) => adminApi.users.remove(u.user_id),
//     filters: { status: status !== "all" ? status : undefined },
//   });
//   <Input value={t.search} onChange={(e) => t.setSearch(e.target.value)} />
//   <ConfirmDialog open={!!t.deleteTarget} onConfirm={t.confirmDelete} isLoading={t.isDeleting} ... />

export interface DataTableParams {
  /** 1-based page index. */
  page: number;
  pageSize: number;
  search: string;
  sortBy?: string;
  sortDesc?: boolean;
  /** Page-specific filters (e.g. { status, source_type }); passed straight through to the fetcher. */
  filters: Record<string, unknown>;
}

export interface DataTablePage<T> {
  items: T[];
  totalCount: number;
}

export interface UseDataTableStateOptions<T> {
  /** Fetches one page. Reads the params and returns the page + total count. */
  fetcher: (params: DataTableParams) => Promise<DataTablePage<T>>;
  /** Optional delete handler; wired to `confirmDelete()`. */
  onDelete?: (item: T) => Promise<void>;
  /** Extra page-specific filters. A change resets to page 1 and refetches. */
  filters?: Record<string, unknown>;
  pageSize?: number;
  debounceMs?: number;
  initialSortBy?: string;
  initialSortDesc?: boolean;
  /** Called when the fetcher or delete throws (e.g. to toast). */
  onError?: (err: unknown) => void;
}

export interface UseDataTableStateResult<T> {
  data: T[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  search: string;
  sortBy: string | undefined;
  sortDesc: boolean;
  deleteTarget: T | null;
  isDeleting: boolean;
  setSearch: (s: string) => void;
  setPage: (p: number) => void;
  setSort: (sortBy: string, sortDesc: boolean) => void;
  setDeleteTarget: (item: T | null) => void;
  /** Runs `onDelete` against the current `deleteTarget`, then clears it and refetches. */
  confirmDelete: () => Promise<void>;
  refetch: () => void;
}

export function useDataTableState<T>(
  options: UseDataTableStateOptions<T>
): UseDataTableStateResult<T> {
  const {
    fetcher,
    onDelete,
    pageSize = 20,
    debounceMs = 300,
    initialSortBy,
    initialSortDesc = false,
    onError,
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<string | undefined>(initialSortBy);
  const [sortDesc, setSortDesc] = useState(initialSortDesc);
  const [deleteTarget, setDeleteTarget] = useState<T | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Serialize filters so a value change (not a new object identity) drives the effects.
  const filters = options.filters ?? {};
  const filtersKey = JSON.stringify(filters);

  // Keep the latest callbacks/filters without retriggering the fetch effect on identity.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Debounce the search box.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), debounceMs);
    return () => window.clearTimeout(id);
  }, [search, debounceMs]);

  // A new search or filter resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filtersKey]);

  // Fetch on any param change (or explicit refetch).
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetcherRef
      .current({ page, pageSize, search: debouncedSearch, sortBy, sortDesc, filters: filtersRef.current })
      .then((res) => {
        if (cancelled) return;
        setData(res.items);
        setTotalCount(res.totalCount);
      })
      .catch((err) => {
        if (cancelled) return;
        onErrorRef.current?.(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, debouncedSearch, sortBy, sortDesc, filtersKey, reloadKey]);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  const setSort = useCallback((by: string, desc: boolean) => {
    setSortBy(by);
    setSortDesc(desc);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(deleteTarget);
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      onErrorRef.current?.(err);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, onDelete, refetch]);

  return {
    data,
    totalCount,
    totalPages: Math.max(1, Math.ceil(totalCount / pageSize)),
    isLoading,
    page,
    pageSize,
    search,
    sortBy,
    sortDesc,
    deleteTarget,
    isDeleting,
    setSearch,
    setPage,
    setSort,
    setDeleteTarget,
    confirmDelete,
    refetch,
  };
}
